/**
 *
 * BulkImportManager
 *
 */

import { memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';

import reducer from './reducer';
import saga from './saga';
import { makeSelectShouldShowPageLeaveWarning } from './selectors';
import { resetAllAct, cleanupAct } from './actions';
import { REDUCER_KEY } from './constants';

const key = REDUCER_KEY;

export function BulkImportManager({ children }) {
  useInjectReducer({ key, reducer });
  useInjectSaga({ key, saga });

  const dispatch = useDispatch();
  const shouldShowWarning = useSelector(makeSelectShouldShowPageLeaveWarning());

  // Show warning before leaving page during active operations
  useEffect(() => {
    const handleBeforeUnload = event => {
      if (shouldShowWarning) {
        const message =
          'You have an ongoing bulk import operation. Are you sure you want to leave?';
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = message;
        return message;
      }
      return undefined;
    };

    if (shouldShowWarning) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldShowWarning]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      dispatch(cleanupAct());
      dispatch(resetAllAct());
    },
    [dispatch],
  );

  return children;
}

BulkImportManager.propTypes = {
  children: PropTypes.node.isRequired,
};

export default memo(BulkImportManager);
